import { type FormEvent, type JSX, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import { Badge, Button, Card, Input, Skeleton, Spinner } from '@/components/ui';
import {
  useDeleteTeamMember,
  useInviteTeamMember,
  useResendTeamInvitation,
  useTeam,
  useUpdateTeamMember,
  useUpdateWorkspace,
  useWorkspace,
  type InviteTeamMemberInput,
  type TeamMember,
  type UserRole,
  type Workspace,
} from '@/hooks/useSettings';
import { useAuth } from '@/hooks/useAuth';
import { api, ApiError } from '@/lib/api';
import { formatRelativeTime } from '@/lib/format';
import { can } from '@/lib/permissions';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Tabs — driven by `?tab=` so deep linking + back button work without
// nesting routes. The tab list is closed (no dynamic registration) so the
// type narrowing covers every case.
// ---------------------------------------------------------------------------
const TABS = ['workspace', 'team', 'security', 'billing', 'api_keys'] as const;
type TabKey = (typeof TABS)[number];

function isTab(s: string | null): s is TabKey {
  return s != null && (TABS as readonly string[]).includes(s);
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function SettingsPage(): JSX.Element {
  const { t } = useTranslation('default');
  const [params, setParams] = useSearchParams();
  const initial = isTab(params.get('tab')) ? (params.get('tab') as TabKey) : 'workspace';
  const [tab, setTab] = useState<TabKey>(initial);

  // Keep URL in sync. Replace (not push) so the browser back button doesn't
  // walk through every tab the operator clicked.
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (tab === 'workspace') next.delete('tab');
    else next.set('tab', tab);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const workspace = useWorkspace();

  return (
    <div className="px-8 py-8 max-w-[1100px] mx-auto">
      <header className="mb-6">
        <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-tight m-0">
          {t('nav_settings')}
        </h1>
        <p className="text-[13px] text-text-3 mt-1.5">
          {workspace.data?.name
            ? `${workspace.data.name} · ${t('settings_workspace')}`
            : t('settings_sub')}
        </p>
      </header>

      {/* Tab bar — Volt underline on active tab, mirrors the design handoff.
          flex-wrap keeps the row on narrow viewports without rendering an
          ugly OS scrollbar; the 6 tabs we ship today comfortably fit on a
          1024px+ window. */}
      <div role="tablist" className="flex flex-wrap gap-1 border-b border-line mb-6">
        {TABS.map((key) => {
          const active = tab === key;
          return (
            <button
              key={key}
              role="tab"
              aria-selected={active}
              onClick={() => setTab(key)}
              className={cn(
                'relative px-4 py-2.5 text-[13px] font-medium whitespace-nowrap transition-colors',
                active ? 'text-text' : 'text-text-3 hover:text-text-2',
              )}
            >
              {t(`settings_tab_${key}`)}
              {active && (
                <span
                  aria-hidden="true"
                  className="absolute left-3 right-3 -bottom-px h-[2px] bg-volt rounded-pill"
                />
              )}
            </button>
          );
        })}
      </div>

      {tab === 'workspace' && <WorkspaceTab workspace={workspace.data} loading={workspace.isLoading} />}
      {tab === 'team' && <TeamTab />}
      {tab === 'security' && <SecurityTab />}
      {tab === 'billing' && <RedirectCard
        title={t('settings_billing_redirect_title')}
        description={t('settings_billing_redirect_desc')}
        cta={t('settings_billing_redirect_cta')}
        to="/billing"
      />}
      {tab === 'api_keys' && <RedirectCard
        title={t('settings_apikeys_redirect_title')}
        description={t('settings_apikeys_redirect_desc')}
        cta={t('settings_apikeys_redirect_cta')}
        to="/developers"
      />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Workspace tab — editable form.
// ---------------------------------------------------------------------------
interface WorkspaceTabProps {
  workspace: Workspace | undefined;
  loading: boolean;
}

function WorkspaceTab({ workspace, loading }: WorkspaceTabProps): JSX.Element {
  const { t } = useTranslation('default');
  const { user } = useAuth();
  const update = useUpdateWorkspace();
  // Operators / viewers see the workspace details but can't edit them.
  // Disable inputs + the Save button rather than hiding the whole tab —
  // the metadata (workspace ID, type, status) is useful even if the
  // user can't change anything.
  const canEdit = can.editWorkspace(user?.role);

  // Form fields are local state seeded from the loaded workspace. We don't
  // mirror the cache directly so the operator can edit, abandon, and the
  // displayed values stay coherent until they hit Save.
  const [name, setName] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [legalName, setLegalName] = useState('');
  const [country, setCountry] = useState('');
  const [timezone, setTimezone] = useState('');
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!workspace) return;
    setName(workspace.name);
    setSupportEmail(workspace.support_email ?? '');
    setLegalName(workspace.legal_name ?? '');
    setCountry(workspace.country ?? '');
    setTimezone(workspace.timezone ?? '');
  }, [workspace]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await update.mutateAsync({
        name: name.trim(),
        support_email: supportEmail.trim(),
        legal_name: legalName.trim(),
        country: country.trim().toUpperCase().slice(0, 2),
        timezone: timezone.trim(),
      });
      setSavedAt(Date.now());
      window.setTimeout(() => setSavedAt(null), 3000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('settings_err_generic'));
    }
  };

  if (loading || !workspace) {
    return (
      <Card padding="lg">
        <Skeleton className="h-6 w-48 mb-4" />
        <Skeleton className="h-10 w-full mb-3" />
        <Skeleton className="h-10 w-full mb-3" />
        <Skeleton className="h-10 w-full" />
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card padding="lg" className="lg:col-span-2">
        <h2 className="display text-[16px] font-semibold text-text mb-4">
          {t('settings_workspace_form_title')}
        </h2>
        {error && (
          <div className="px-3.5 py-3 mb-4 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger">
            {error}
          </div>
        )}
        {savedAt && (
          <div className="px-3.5 py-3 mb-4 bg-success-bg border border-success-bd rounded-md text-[13px] text-success">
            {t('settings_workspace_saved')}
          </div>
        )}
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
          <Field label={t('settings_workspace_name')}>
            <Input value={name} onChange={(e) => setName(e.target.value)} disabled={update.isPending || !canEdit} />
          </Field>
          <Field label={t('settings_workspace_legal')} hint={t('settings_workspace_legal_hint')}>
            <Input value={legalName} onChange={(e) => setLegalName(e.target.value)} disabled={update.isPending || !canEdit} />
          </Field>
          <Field label={t('settings_workspace_support_email')}>
            <Input
              type="email"
              value={supportEmail}
              onChange={(e) => setSupportEmail(e.target.value)}
              disabled={update.isPending || !canEdit}
              placeholder="support@yourbrand.com"
            />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={t('settings_workspace_country')} hint={t('settings_workspace_country_hint')}>
              <Input
                value={country}
                onChange={(e) => setCountry(e.target.value.toUpperCase().slice(0, 2))}
                disabled={update.isPending || !canEdit}
                placeholder="US"
                maxLength={2}
              />
            </Field>
            <Field label={t('settings_workspace_timezone')} hint={t('settings_workspace_timezone_hint')}>
              <Input
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                disabled={update.isPending || !canEdit}
                placeholder="America/New_York"
              />
            </Field>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="submit" disabled={update.isPending || !canEdit}>
              {update.isPending ? (
                <>
                  <Spinner />
                  <span>{t('settings_workspace_saving')}</span>
                </>
              ) : (
                t('settings_workspace_save')
              )}
            </Button>
          </div>
        </form>
      </Card>

      <Card padding="lg">
        <h2 className="display text-[14px] font-semibold text-text mb-3">
          {t('settings_workspace_meta_title')}
        </h2>
        <dl className="space-y-3 text-[13px]">
          <MetaRow label={t('settings_workspace_meta_id')} value={`#${workspace.id}`} mono />
          <MetaRow label={t('settings_workspace_meta_type')} value={workspace.type} />
          <MetaRow label={t('settings_workspace_meta_status')} value={workspace.status} />
          <MetaRow
            label={t('settings_workspace_meta_created')}
            value={new Date(workspace.created_at).toLocaleDateString()}
          />
        </dl>
      </Card>
    </div>
  );
}

interface FieldProps {
  label: string;
  hint?: string;
  children: JSX.Element;
}

function Field({ label, hint, children }: FieldProps): JSX.Element {
  return (
    <div>
      <label className="block text-[12px] font-medium text-text-2 mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-[12px] text-text-4 mt-1">{hint}</p>}
    </div>
  );
}

function MetaRow({ label, value, mono }: { label: string; value: string; mono?: boolean }): JSX.Element {
  return (
    <div className="flex justify-between items-baseline gap-3">
      <dt className="text-text-3">{label}</dt>
      <dd className={cn('text-text font-medium', mono && 'mono text-[12px]')}>{value}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Security tab — self-service password change. Distinct from the
// /forgot-password email flow: this requires the user's CURRENT
// password, not an email-delivered token. POST /v1/auth/change-password
// returns 204 on success and 401 on a wrong current password.
// ---------------------------------------------------------------------------
function SecurityTab(): JSX.Element {
  const { t } = useTranslation('default');
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setCurrent('');
    setNext('');
    setConfirm('');
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Client-side validation matches what the service enforces (length
    // 8-72 + new differs from current). Confirm-match is purely a UX
    // belt — we never send `confirm` to the server.
    if (next.length < 8) {
      setError(t('settings_pw_err_too_short'));
      return;
    }
    if (next.length > 72) {
      setError(t('settings_pw_err_too_long'));
      return;
    }
    if (next !== confirm) {
      setError(t('settings_pw_err_mismatch'));
      return;
    }
    if (next === current) {
      setError(t('settings_pw_err_same'));
      return;
    }

    setSubmitting(true);
    try {
      await api.post<void>('/v1/auth/change-password', {
        current_password: current,
        new_password: next,
      });
      setSavedAt(Date.now());
      reset();
      window.setTimeout(() => setSavedAt(null), 4000);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401 || err.code === 'invalid_credentials') {
          setError(t('settings_pw_err_wrong_current'));
        } else {
          setError(err.message);
        }
      } else {
        setError(t('settings_err_generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card padding="lg" className="max-w-[560px]">
      <h2 className="display text-[16px] font-semibold text-text mb-1">
        {t('settings_pw_title')}
      </h2>
      <p className="text-[13px] text-text-3 mb-5">
        {t('settings_pw_sub')}
      </p>
      {error && (
        <div className="px-3.5 py-3 mb-4 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger">
          {error}
        </div>
      )}
      {savedAt && (
        <div className="px-3.5 py-3 mb-4 bg-success-bg border border-success-bd rounded-md text-[13px] text-success">
          {t('settings_pw_saved')}
        </div>
      )}
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4" autoComplete="off">
        <Field label={t('settings_pw_current')}>
          <Input
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            disabled={submitting}
          />
        </Field>
        <Field label={t('settings_pw_new')} hint={t('settings_pw_new_hint')}>
          <Input
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            disabled={submitting}
          />
        </Field>
        <Field label={t('settings_pw_confirm')}>
          <Input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={submitting}
          />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="submit" disabled={submitting || !current || !next || !confirm}>
            {submitting ? (
              <>
                <Spinner />
                <span>{t('settings_pw_saving')}</span>
              </>
            ) : (
              t('settings_pw_save')
            )}
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Team tab — list members. Invite/role-change comes with admin module.
// ---------------------------------------------------------------------------
function TeamTab(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);
  const { user } = useAuth();
  const team = useTeam();
  const items = team.data?.items ?? [];
  const [inviteOpen, setInviteOpen] = useState(false);
  const canInvite = can.inviteMembers(user?.role);

  return (
    <Card padding="none">
      <div className="px-5 py-4 border-b border-line flex items-center justify-between gap-3">
        <div>
          <h2 className="display text-[16px] font-semibold text-text">{t('settings_team_title')}</h2>
          <p className="text-[12px] text-text-3 mt-0.5">
            {t('settings_team_count', { total: items.length.toLocaleString(lang) })}
          </p>
        </div>
        {canInvite && (
          <Button onClick={() => setInviteOpen(true)} size="sm">
            {t('settings_team_invite_btn')}
          </Button>
        )}
      </div>
      {inviteOpen && <InviteMemberModal onClose={() => setInviteOpen(false)} />}
      {team.isLoading ? (
        <div className="p-5 space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center text-[13px] text-text-3">
          {t('settings_team_empty')}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                <th className="px-5 py-2.5 font-medium">{t('settings_team_col_member')}</th>
                <th className="px-5 py-2.5 font-medium">{t('settings_team_col_role')}</th>
                <th className="px-5 py-2.5 font-medium">{t('settings_team_col_status')}</th>
                <th className="px-5 py-2.5 font-medium">{t('settings_team_col_last_login')}</th>
                <th className="px-5 py-2.5 font-medium">{t('settings_team_col_joined')}</th>
                <th className="px-5 py-2.5 font-medium text-right pr-6">{t('settings_team_col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <TeamRow
                  key={m.id}
                  m={m}
                  lang={lang}
                  currentUserId={user?.uid ?? 0}
                  canManage={canInvite}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Invite member modal — POST /v1/settings/team. Backend creates a
// passwordless user + emails them a "set your password" link (same
// page as forgot-password). On success we close + the team list
// invalidates so the new pending member shows up in the table.
// ---------------------------------------------------------------------------
function InviteMemberModal({ onClose }: { onClose: () => void }): JSX.Element {
  const { t } = useTranslation('default');
  const invite = useInviteTeamMember();
  const [form, setForm] = useState<InviteTeamMemberInput>({
    email: '',
    full_name: '',
    role: 'admin',
  });
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await invite.mutateAsync({
        email: form.email.trim().toLowerCase(),
        full_name: form.full_name.trim(),
        role: form.role,
      });
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'email_in_use') {
          setError(t('settings_team_invite_err_in_use'));
        } else {
          setError(err.message);
        }
      } else {
        setError(t('settings_err_generic'));
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink/50 px-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-[460px] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-line">
          <h2 className="display text-[16px] font-semibold text-text">{t('settings_team_invite_title')}</h2>
          <p className="text-[12px] text-text-3 mt-0.5">{t('settings_team_invite_sub')}</p>
        </div>
        <form onSubmit={(e) => void submit(e)} className="px-5 py-4 space-y-4">
          {error && (
            <div className="px-3 py-2 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger">
              {error}
            </div>
          )}
          <Field label={t('settings_team_invite_email')}>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              placeholder="teammate@company.com"
              required
              autoComplete="off"
            />
          </Field>
          <Field label={t('settings_team_invite_name')} hint={t('settings_team_invite_name_hint')}>
            <Input
              value={form.full_name}
              onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))}
              placeholder="Jane Smith"
              autoComplete="off"
            />
          </Field>
          <Field label={t('settings_team_invite_role')} hint={t('settings_team_invite_role_hint')}>
            <select
              value={form.role}
              onChange={(e) =>
                setForm((p) => ({ ...p, role: e.target.value as InviteTeamMemberInput['role'] }))
              }
              className="w-full h-9 px-3 rounded-md border border-line-strong bg-surface text-[13px] text-text focus:border-volt-600 focus:outline-none focus:ring-2 focus:ring-volt-glow"
            >
              <option value="admin">{t('settings_team_role_admin')}</option>
              <option value="operator">{t('settings_team_role_operator')}</option>
              <option value="viewer">{t('settings_team_role_viewer')}</option>
            </select>
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={invite.isPending || !form.email}>
              {invite.isPending ? (
                <>
                  <Spinner />
                  <span>{t('settings_team_invite_sending')}</span>
                </>
              ) : (
                t('settings_team_invite_send')
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

const ROLE_VARIANT: Record<UserRole, 'volt' | 'info' | 'gray' | 'neutral'> = {
  owner: 'volt',
  admin: 'info',
  operator: 'neutral',
  viewer: 'gray',
  api: 'gray',
};

type RowModal = 'role' | 'deactivate' | 'activate' | 'delete' | 'resend' | null;

function TeamRow({
  m,
  lang,
  currentUserId,
  canManage,
}: {
  m: TeamMember;
  lang: string;
  currentUserId: number;
  canManage: boolean;
}): JSX.Element {
  const { t } = useTranslation('default');
  const [modal, setModal] = useState<RowModal>(null);

  const isSelf = m.id === currentUserId;
  const isOwner = m.role === 'owner';
  // Owner is always immutable. Self can be role-changed by another
  // admin but never deactivated/deleted — and the user themself can't
  // do either action on their own row.
  const canMutate = canManage && !isOwner;
  const canDeactivate = canMutate && !isSelf;
  const canDelete = canMutate && !isSelf;
  // "Pending invite" heuristic — the user accepted the invite but
  // never logged in. Resend lets the inviter re-mint the link if the
  // recipient lost it.
  const isPending = !m.last_login_at;

  // Initials for the avatar gradient — first letter of full_name, fallback to email.
  const seed = (m.full_name || m.email).trim();
  const initials = seed
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('') || '?';
  return (
    <>
    <tr className="border-b border-line last:border-0 hover:bg-surface-2">
      <td className="px-5 py-3">
        <div className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className="w-8 h-8 rounded-full grid place-items-center text-white text-[11px] font-bold flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, var(--volt-500), var(--volt-700))' }}
          >
            {initials}
          </span>
          <div className="min-w-0">
            <div className="text-text font-medium truncate">
              {m.full_name || m.email}
              {isSelf && <span className="ml-2 text-[11px] text-text-4">{t('settings_team_self')}</span>}
            </div>
            {m.full_name && (
              <div className="text-[12px] text-text-3 truncate">{m.email}</div>
            )}
          </div>
        </div>
      </td>
      <td className="px-5 py-3">
        <Badge variant={ROLE_VARIANT[m.role]}>{t(`settings_role_${m.role}`)}</Badge>
      </td>
      <td className="px-5 py-3">
        {!m.is_active ? (
          <Badge variant="neutral">{t('settings_team_status_inactive')}</Badge>
        ) : isPending ? (
          <Badge variant="info">{t('settings_team_status_pending')}</Badge>
        ) : (
          <Badge variant="success">{t('settings_team_status_active')}</Badge>
        )}
      </td>
      <td className="px-5 py-3 text-text-3 whitespace-nowrap text-[12px]">
        {m.last_login_at ? formatRelativeTime(m.last_login_at, lang) : '—'}
      </td>
      <td className="px-5 py-3 text-text-3 whitespace-nowrap text-[12px]">
        {formatRelativeTime(m.created_at, lang)}
      </td>
      <td className="px-5 py-3 text-right pr-4">
        {canMutate ? (
          <RowActionsMenu
            isPending={isPending}
            isActive={m.is_active}
            canDeactivate={canDeactivate}
            canDelete={canDelete}
            onPick={(action) => setModal(action)}
          />
        ) : (
          <span className="text-text-4 text-[12px]">—</span>
        )}
      </td>
    </tr>
    {modal === 'role' && <ChangeRoleModal m={m} onClose={() => setModal(null)} />}
    {modal === 'deactivate' && <ConfirmActiveModal m={m} active={false} onClose={() => setModal(null)} />}
    {modal === 'activate' && <ConfirmActiveModal m={m} active={true} onClose={() => setModal(null)} />}
    {modal === 'delete' && <ConfirmDeleteModal m={m} onClose={() => setModal(null)} />}
    {modal === 'resend' && <ConfirmResendModal m={m} onClose={() => setModal(null)} />}
    </>
  );
}

// RowActionsMenu — anchors a cog button to a portal-rendered popup.
// Portal escapes the table's overflow-x-auto container (which would
// otherwise clip the dropdown). Position is recomputed on open from
// the button's getBoundingClientRect so the menu lands right under it.
// Closes on outside click + Escape.
function RowActionsMenu({
  isPending,
  isActive,
  canDeactivate,
  canDelete,
  onPick,
}: {
  isPending: boolean;
  isActive: boolean;
  canDeactivate: boolean;
  canDelete: boolean;
  onPick: (a: 'role' | 'deactivate' | 'activate' | 'delete' | 'resend') => void;
}): JSX.Element {
  const { t } = useTranslation('default');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; right: number } | null>(null);

  // Recompute on open + on scroll/resize while open. fixed positioning
  // means the menu doesn't follow scroll automatically — re-anchoring
  // is cheaper than rolling our own position-sticky logic.
  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      setCoords({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    };
    reposition();
    // Close on outside click. The menu lives in a portal under
    // document.body, NOT inside buttonRef, so we have to check both
    // refs — otherwise clicking a menu item itself triggers
    // setOpen(false) before its onClick runs (mousedown fires before
    // click in the bubbling order).
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = (action: 'role' | 'deactivate' | 'activate' | 'delete' | 'resend') => {
    setOpen(false);
    onPick(action);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-7 h-7 grid place-items-center rounded-md hover:bg-surface-3 text-text-3 hover:text-text"
        aria-label={t('settings_team_col_actions')}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Icon.cog width="16" height="16" />
      </button>
      {open && coords && createPortal(
        <div
          ref={menuRef}
          role="menu"
          className="fixed z-[1000] min-w-[200px] bg-surface border border-line rounded-md shadow-lg py-1 text-left"
          style={{ top: coords.top, right: coords.right }}
        >
          <RowMenuItem onClick={() => pick('role')}>
            {t('settings_team_action_role')}
          </RowMenuItem>
          {isPending && (
            <RowMenuItem onClick={() => pick('resend')}>
              {t('settings_team_action_resend')}
            </RowMenuItem>
          )}
          {canDeactivate && isActive && (
            <RowMenuItem onClick={() => pick('deactivate')}>
              {t('settings_team_action_deactivate')}
            </RowMenuItem>
          )}
          {canDeactivate && !isActive && (
            <RowMenuItem onClick={() => pick('activate')}>
              {t('settings_team_action_activate')}
            </RowMenuItem>
          )}
          {canDelete && (
            <RowMenuItem destructive onClick={() => pick('delete')}>
              {t('settings_team_action_delete')}
            </RowMenuItem>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}

function RowMenuItem({
  children,
  destructive,
  onClick,
}: {
  children: React.ReactNode;
  destructive?: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full text-left px-3 py-1.5 text-[13px] hover:bg-surface-2',
        destructive ? 'text-danger hover:text-danger' : 'text-text',
      )}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Row action modals
// ---------------------------------------------------------------------------

function ChangeRoleModal({ m, onClose }: { m: TeamMember; onClose: () => void }): JSX.Element {
  const { t } = useTranslation('default');
  const update = useUpdateTeamMember();
  const [role, setRole] = useState<'admin' | 'operator' | 'viewer'>(
    (m.role === 'admin' || m.role === 'operator' || m.role === 'viewer') ? m.role : 'viewer',
  );
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await update.mutateAsync({ id: m.id, role });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('settings_err_generic'));
    }
  };

  return (
    <ModalShell title={t('settings_team_role_modal_title', { name: m.full_name || m.email })} onClose={onClose}>
      <form onSubmit={(e) => void submit(e)} className="space-y-4">
        {error && <div className="px-3 py-2 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger">{error}</div>}
        <Field label={t('settings_team_invite_role')}>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as 'admin' | 'operator' | 'viewer')}
            className="w-full h-9 px-3 rounded-md border border-line-strong bg-surface text-[13px] text-text focus:border-volt-600 focus:outline-none focus:ring-2 focus:ring-volt-glow"
          >
            <option value="admin">{t('settings_team_role_admin')}</option>
            <option value="operator">{t('settings_team_role_operator')}</option>
            <option value="viewer">{t('settings_team_role_viewer')}</option>
          </select>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>{t('cancel')}</Button>
          <Button type="submit" disabled={update.isPending || role === m.role}>
            {update.isPending ? <><Spinner /><span>{t('settings_team_invite_sending')}</span></> : t('settings_team_role_save')}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function ConfirmActiveModal({
  m,
  active,
  onClose,
}: {
  m: TeamMember;
  active: boolean;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation('default');
  const update = useUpdateTeamMember();
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    try {
      await update.mutateAsync({ id: m.id, is_active: active });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('settings_err_generic'));
    }
  };

  const titleKey = active ? 'settings_team_activate_title' : 'settings_team_deactivate_title';
  const descKey = active ? 'settings_team_activate_desc' : 'settings_team_deactivate_desc';
  const ctaKey = active ? 'settings_team_action_activate' : 'settings_team_action_deactivate';

  return (
    <ModalShell title={t(titleKey, { name: m.full_name || m.email })} onClose={onClose}>
      {error && <div className="px-3 py-2 mb-3 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger">{error}</div>}
      <p className="text-[13px] text-text-3 mb-5">{t(descKey)}</p>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>{t('cancel')}</Button>
        <Button type="button" variant={active ? 'primary' : 'danger'} onClick={() => void submit()} disabled={update.isPending}>
          {update.isPending ? <><Spinner /><span>{t('settings_team_invite_sending')}</span></> : t(ctaKey)}
        </Button>
      </div>
    </ModalShell>
  );
}

function ConfirmDeleteModal({ m, onClose }: { m: TeamMember; onClose: () => void }): JSX.Element {
  const { t } = useTranslation('default');
  const del = useDeleteTeamMember();
  const [error, setError] = useState<string | null>(null);
  const [needsDeactivate, setNeedsDeactivate] = useState(false);

  const submit = async () => {
    setError(null);
    setNeedsDeactivate(false);
    try {
      await del.mutateAsync(m.id);
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'in_use') {
        // Backend says delete blocked because user has historical
        // references — UI explicitly offers the deactivate fallback.
        setNeedsDeactivate(true);
        setError(t('settings_team_delete_in_use'));
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(t('settings_err_generic'));
      }
    }
  };

  return (
    <ModalShell title={t('settings_team_delete_title', { name: m.full_name || m.email })} onClose={onClose}>
      {error && <div className="px-3 py-2 mb-3 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger">{error}</div>}
      <p className="text-[13px] text-text-3 mb-5">{t('settings_team_delete_desc')}</p>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>{t('cancel')}</Button>
        {!needsDeactivate ? (
          <Button type="button" variant="danger" onClick={() => void submit()} disabled={del.isPending}>
            {del.isPending ? <><Spinner /><span>{t('settings_team_invite_sending')}</span></> : t('settings_team_action_delete')}
          </Button>
        ) : (
          <Button type="button" variant="danger" onClick={onClose}>
            {t('settings_team_delete_close')}
          </Button>
        )}
      </div>
    </ModalShell>
  );
}

function ConfirmResendModal({ m, onClose }: { m: TeamMember; onClose: () => void }): JSX.Element {
  const { t } = useTranslation('default');
  const resend = useResendTeamInvitation();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setError(null);
    try {
      await resend.mutateAsync(m.id);
      setDone(true);
      window.setTimeout(onClose, 1800);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('settings_err_generic'));
    }
  };

  return (
    <ModalShell title={t('settings_team_resend_title')} onClose={onClose}>
      {error && <div className="px-3 py-2 mb-3 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger">{error}</div>}
      {done ? (
        <div className="px-3 py-2 mb-3 bg-success-bg border border-success-bd rounded-md text-[13px] text-success">
          {t('settings_team_resend_done', { email: m.email })}
        </div>
      ) : (
        <p className="text-[13px] text-text-3 mb-5">{t('settings_team_resend_desc', { email: m.email })}</p>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>{done ? t('settings_team_delete_close') : t('cancel')}</Button>
        {!done && (
          <Button type="button" onClick={() => void submit()} disabled={resend.isPending}>
            {resend.isPending ? <><Spinner /><span>{t('settings_team_invite_sending')}</span></> : t('settings_team_action_resend')}
          </Button>
        )}
      </div>
    </ModalShell>
  );
}

function ModalShell({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink/50 px-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-[440px] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-line">
          <h2 className="display text-[16px] font-semibold text-text">{title}</h2>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Redirect card — used for the Billing + API keys tabs that point the
// operator at the dedicated pages instead of duplicating their UI here.
// ---------------------------------------------------------------------------
interface RedirectCardProps {
  title: string;
  description: string;
  cta: string;
  to: string;
}

function RedirectCard({ title, description, cta, to }: RedirectCardProps): JSX.Element {
  const navigate = useNavigate();
  return (
    <Card padding="lg" className="flex items-start gap-4">
      <span
        aria-hidden="true"
        className="flex-shrink-0 w-10 h-10 rounded-md grid place-items-center bg-volt/10 text-volt-600"
      >
        <Icon.arrowRight width="20" height="20" />
      </span>
      <div className="flex-1">
        <h2 className="display text-[16px] font-semibold text-text">{title}</h2>
        <p className="text-[13px] text-text-3 mt-1">{description}</p>
      </div>
      <Button variant="secondary" onClick={() => navigate(to)}>
        {cta}
      </Button>
    </Card>
  );
}
